export const POST_LOGIN_EXPERIENCE_STORAGE_KEY = 'soc_post_login_experience:v1';
const UTM_CONTENT_QUERY_PARAM = 'utm_content';
export const MAX_POST_LOGIN_UTM_CONTENT_LENGTH = 64;
const MAX_RAW_UTM_CONTENT_LENGTH = 128;
const SAFE_UTM_CONTENT_PATTERN = /^[a-z0-9](?:[a-z0-9_-]{0,63})$/;

const canUseWindow = () => typeof window !== 'undefined';

const getSessionStorage = () => {
  if (!canUseWindow()) return null;

  try {
    return window.sessionStorage || null;
  } catch (_error) {
    return null;
  }
};

/**
 * Keep safe, unrecognized values so exit routing can emit UNKNOWN_UTM without
 * persisting attacker-controlled markup, control characters, or oversized data.
 */
const normalizeUtmContent = (value) => {
  const rawValue = String(value ?? '');
  if (!rawValue || rawValue.length > MAX_RAW_UTM_CONTENT_LENGTH) return '';

  const normalized = rawValue.trim();
  if (
    !normalized ||
    normalized.length > MAX_POST_LOGIN_UTM_CONTENT_LENGTH ||
    !SAFE_UTM_CONTENT_PATTERN.test(normalized)
  ) {
    return '';
  }

  return normalized;
};

const readUtmContentFromUrl = (url = canUseWindow() ? window.location.href : '') => {
  if (!url) return '';

  try {
    const parsedUrl = new URL(
      url,
      canUseWindow() ? window.location.origin : 'https://portal.local'
    );
    const utmContentValues = parsedUrl.searchParams.getAll(
      UTM_CONTENT_QUERY_PARAM
    );
    if (utmContentValues.length !== 1) return '';

    return normalizeUtmContent(utmContentValues[0]);
  } catch (_error) {
    return '';
  }
};

const parseStoredPostLoginExperience = (rawValue) => {
  if (!rawValue) return null;

  try {
    const parsedValue = JSON.parse(rawValue);
    const utmContent = normalizeUtmContent(parsedValue?.utm_content);
    return utmContent ? { utm_content: utmContent } : null;
  } catch (_error) {
    return null;
  }
};

const getStoredPostLoginExperience = () => {
  const storage = getSessionStorage();
  if (!storage) return null;

  try {
    return parseStoredPostLoginExperience(
      storage.getItem(POST_LOGIN_EXPERIENCE_STORAGE_KEY)
    );
  } catch (_error) {
    return null;
  }
};

const storePostLoginUtmContent = (utmContentValue, now = () => new Date()) => {
  const utmContent = normalizeUtmContent(utmContentValue);
  const storage = getSessionStorage();
  if (!utmContent || !storage) return null;

  const storedExperience = getStoredPostLoginExperience();
  if (storedExperience?.utm_content === utmContent) {
    return utmContent;
  }

  try {
    storage.setItem(
      POST_LOGIN_EXPERIENCE_STORAGE_KEY,
      JSON.stringify({
        utm_content: utmContent,
        captured_at: now().toISOString(),
      })
    );
  } catch (_error) {
    return null;
  }

  return utmContent;
};

export const capturePostLoginUtmContent = ({
  url = canUseWindow() ? window.location.href : '',
  now,
  clearStoredWhenMissing = false,
} = {}) => {
  const utmContentFromUrl = readUtmContentFromUrl(url);
  if (utmContentFromUrl) {
    return storePostLoginUtmContent(utmContentFromUrl, now);
  }

  if (clearStoredWhenMissing) {
    const storage = getSessionStorage();
    try {
      storage?.removeItem(POST_LOGIN_EXPERIENCE_STORAGE_KEY);
    } catch (_error) {
      // Storage can be unavailable in private browsing or hardened contexts.
    }
    return null;
  }

  return getStoredPostLoginExperience()?.utm_content || null;
};

export const getPostLoginUtmContent = () =>
  getStoredPostLoginExperience()?.utm_content || null;

export const clearPostLoginUtmContent = () => {
  const storage = getSessionStorage();
  try {
    storage?.removeItem(POST_LOGIN_EXPERIENCE_STORAGE_KEY);
  } catch (_error) {
    // Storage can be unavailable in private browsing or hardened contexts.
  }
};

export const syncPostLoginExperienceCapture = ({
  url,
  now,
  clearStoredWhenMissing,
  setPostLoginExperience,
} = {}) => {
  const utmContent =
    capturePostLoginUtmContent({ url, now, clearStoredWhenMissing }) || null;

  if (typeof setPostLoginExperience === 'function') {
    setPostLoginExperience({ utmContent });
  }

  return utmContent;
};

export { readUtmContentFromUrl };
